// ================================================================
// apikey-session.ts — 非 SSO 登入的 session 管理
//
// 支援兩種登入來源：
//   1. loginWithApiKey        — 自訂 API Key（aig_xxx），呼叫 /auth/verify-key
//   2. loginWithClientCredentials — Entra Client Credentials（Method B），
//                                   呼叫 /auth/client-credentials
//
// 兩者均回傳 sess_xxx token，儲存於 localStorage
// ================================================================

const STORAGE_KEY = 'ai_platform_apikey_session';

export interface ApiKeySession {
  token:  string;   // sess_xxx（傳給 Workers 做 Bearer）
  upn:    string;
  name:   string;
  tid:    string;
  roles:  string[];
  label?: string;
}

export function getApiKeySession(): ApiKeySession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ApiKeySession;
  } catch {
    return null;
  }
}

export function saveApiKeySession(s: ApiKeySession): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

export function clearApiKeySession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/** 用自訂 API Key（aig_xxx）向 Workers 換取 session token */
export async function loginWithApiKey(
  apiKey: string,
  apiBaseUrl: string
): Promise<ApiKeySession> {
  const res = await fetch(`${apiBaseUrl}/auth/verify-key`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ apiKey }),
  });

  if (!res.ok) {
    const err = await res.json<{ error?: string }>().catch(() => ({}));
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }

  const data = await res.json<ApiKeySession & { token: string }>();
  const session: ApiKeySession = {
    token:  data.token,
    upn:    data.upn,
    name:   data.name,
    tid:    data.tid,
    roles:  data.roles,
    label:  data.label,
  };
  saveApiKeySession(session);
  return session;
}

/**
 * Method B：Entra Client Credentials Flow
 * 使用者提供 Tenant ID + Client ID + Client Secret
 * Workers 代呼叫 Entra，成功後發 sess_xxx session token
 */
export async function loginWithClientCredentials(
  tenantId: string,
  clientId: string,
  clientSecret: string,
  apiBaseUrl: string
): Promise<ApiKeySession> {
  const res = await fetch(`${apiBaseUrl}/auth/client-credentials`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, clientId, clientSecret }),
  });

  if (!res.ok) {
    const err = await res.json<{ error?: string }>().catch(() => ({}));
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }

  const data = await res.json<{ token: string; upn: string; name: string; tid: string; roles: string[] }>();
  const session: ApiKeySession = {
    token: data.token,
    upn:   data.upn,
    name:  data.name,
    tid:   data.tid,
    roles: data.roles,
    label: `Service Principal (${clientId.substring(0, 8)}…)`,
  };
  saveApiKeySession(session);
  return session;
}
